<script setup lang="ts">
import { computed } from 'vue'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-md',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-sm',
        ghost: 'text-muted-foreground hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Glassmorphism variants
        glass: 'bg-white/20 backdrop-blur-md border border-white/30 text-foreground hover:bg-white/30 hover:shadow-lg dark:bg-white/10 dark:border-white/20 dark:hover:bg-white/20',
        'glass-primary': 'bg-primary/80 backdrop-blur-md text-primary-foreground hover:bg-primary/90 hover:shadow-lg',
        'glass-outline': 'bg-transparent backdrop-blur-sm border border-gray-300/50 text-foreground hover:bg-white/20 hover:border-gray-300/70 dark:border-gray-600/50 dark:hover:bg-white/10',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

type ButtonVariants = VariantProps<typeof buttonVariants>

interface Props {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  as?: string
  class?: string
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'default',
  as: 'button',
  type: 'button',
})

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props
  return delegated
})
</script>

<template>
  <component
    :is="as"
    :class="cn(buttonVariants({ variant, size }), props.class)"
    :disabled="disabled"
    :type="as === 'button' ? type : undefined"
  >
    <slot />
  </component>
</template>
